import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const sections = [
	{
		title: "Leadership Skills",
		path: "/leadership-skills",
		headerColor: "bg-primary",
		accentColor: "text-accent",
		image: "/leadershipVector.png",
		description:
			"Ten core leadership principles — from Student Obsession to Integrity — backed by real outcomes and measurable impact.",
		cta: "View Leadership Skills",
	},
	{
		title: "Experience & Projects",
		path: "/experience-projects",
		headerColor: "bg-secondary",
		accentColor: "text-accent",
		image: "/experienceVector.png",
		description:
			"A full career timeline spanning instructional technology, web development, international education, and the US Marine Corps.",
		cta: "View Experience",
	},
	{
		title: "Personal Profile",
		path: "/about-me",
		headerColor: "bg-primary",
		accentColor: "text-accent",
		image: "/aboutMeVector.png",
		description:
			"Background, tech stack, soft skills, and education — the full picture of who I am and what I bring to a team.",
		cta: "View Profile",
	},
];

function LandingPage() {
	const navigate = useNavigate();
	const containerRefs = useRef([]);
	const [visibleSections, setVisibleSections] = useState([]);

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const index = parseInt(entry.target.getAttribute("data-index"));
					if (entry.isIntersecting) {
						setVisibleSections((prev) => (prev.includes(index) ? prev : [...prev, index]));
					}
				});
			},
			{ threshold: 0.3 }
		);

		containerRefs.current.forEach((ref) => {
			if (ref) observer.observe(ref);
		});

		return () => {
			containerRefs.current.forEach((ref) => {
				if (ref) observer.unobserve(ref);
			});
		};
	}, []);

	return (
		<div className="bg-slate-50 pt-0">
			<div className="relative z-10">
				<div className="flex flex-col items-center mt-8 pb-16">

					{/* Intro: photo + first paragraph */}
					<div className="w-full max-w-4xl mb-10 px-4 md:px-0">
						<div className="flex flex-col md:flex-row items-center gap-6">
							<div className="w-full md:w-1/4 flex justify-center shrink-0">
								<img
									src="/MyLogo.png"
									alt="Daniel Middleton"
									className="w-28 h-28 md:w-full md:h-auto object-contain rounded-full shadow-sm"
								/>
							</div>
							<div className="w-full md:w-3/4 text-left">
								<p className="text-gray-700 text-sm leading-relaxed">
									I am a multidisciplinary professional with a passion for building effective, learner-centered solutions that fuse technology, design, and education. My leadership experience spans K-12 international schools, corporate teams, and higher education institutions. Whether coordinating instructional design efforts, developing web-based learning tools, or leading a department through change, I take pride in approaching challenges with empathy, innovation, and data-driven decision making.
								</p>
							</div>
						</div>
					</div>
					{sections.map((section, index) => (
						<div
							key={section.path}
							data-index={index}
							ref={(el) => (containerRefs.current[index] = el)}
							className={`transition-all duration-700 ease-out w-full max-w-2xl mb-10 px-4 md:px-0 ${
								visibleSections.includes(index)
									? "opacity-100 translate-y-0"
									: "opacity-0 translate-y-8"
							}`}
						>
							<button
								onClick={() => navigate(section.path)}
								className="group w-full text-left rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
							>
								{/* Card header */}
								<div className={`${section.headerColor} px-8 pt-7 pb-5 flex items-start gap-5`}>
									<img
										src={section.image}
										alt={section.title}
										className="w-16 h-16 object-contain rounded-lg bg-white/10 p-1 shrink-0"
									/>
									<div>
										<h3 className="text-white text-2xl font-bold leading-tight">
											{section.title}
										</h3>
										<p className="text-white/70 text-sm mt-1">{section.description}</p>
									</div>
								</div>

								{/* Card footer */}
								<div className="bg-white px-8 py-4 flex items-center justify-between">
									<span className={`text-sm font-semibold ${section.accentColor} group-hover:underline`}>
										{section.cta}
									</span>
									<span className="text-accent text-lg font-bold group-hover:translate-x-1 transition-transform duration-200">
										→
									</span>
								</div>
							</button>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

export default LandingPage;
